import { DatePicker } from '@/components/commons/date-picker-custom';
import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import { CustomerDetailElementDataType } from '@/components/sections/builder/elements/body/customer/customer_detail/type';
import FieldErrorMessage from '@/components/sections/field-error-message';
import { Button } from '@/components/ui/button';
import { FormControl, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, isEmpty } from '@/lib/utils';
import { StoreCustomerProfilePayload } from '@/types/http';
import { Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

const customerSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().optional(),
  phone: Yup.string()
    .matches(/^62\d+$/, 'Phone numbers must begin with 62')
    .required('Phone number is required')
    .max(15, 'Phone number cannot be longer than 15 characters'),
  gender: Yup.string().optional(),
  email: Yup.string().optional(),
  dob: Yup.date().optional(),
});

const initialValues: StoreCustomerProfilePayload = {
  first_name: '',
  last_name: '',
  phone: '',
  gender: '',
  email: '',
  dob: new Date(),
};

type ModalProfileProps = {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  data?: StoreCustomerProfilePayload;
  onSave?: (values: StoreCustomerProfilePayload) => void;
  content?: CustomerDetailElementDataType;
};

export default function ModalProfile({
  openModal,
  setOpenModal,
  data,
  onSave,
  content,
}: ModalProfileProps) {
  const [defaultValues, setDefaultValues] =
    React.useState<StoreCustomerProfilePayload>(initialValues);

  const handleSubmit = async (values: StoreCustomerProfilePayload) => {
    onSave?.(values);
  };

  React.useEffect(() => {
    if (openModal && data) {
      setDefaultValues(data);
    }
  }, [data, openModal]);

  return (
    <ModalResponsive
      title='Edit profile'
      openModal={openModal}
      setOpenModal={setOpenModal}
      className='theme_font max-w-xl'
    >
      <div
        style={
          {
            '--btn-label': content?.colorScheme?.btnSolidlabel,
            '--border-color': content?.colorScheme?.btnSolidBackgroud,
          } as React.CSSProperties
        }
      >
        <Formik
          initialValues={defaultValues}
          validationSchema={customerSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, handleChange, handleBlur, values, errors, touched }) => (
            <>
              <Form
                onSubmit={handleSubmit}
                className='max-h-[420px] space-y-2 overflow-y-auto px-6 py-5 md:px-12'
              >
                <div className='grid grid-cols-2 gap-4'>
                  <FormItem className='text-sm'>
                    <FormControl>
                      <Input
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.first_name as string}
                        name='first_name'
                        type='text'
                        size={100}
                        placeholder='First name'
                        className={cn(
                          ' text-base font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content?.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.first_name && touched.first_name && 'border-red-500'
                        )}
                        data-testid='edit-firstname'
                      />
                    </FormControl>
                    <FieldErrorMessage error={errors.first_name} />
                  </FormItem>
                  <FormItem className='text-sm'>
                    <FormControl>
                      <Input
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name='last_name'
                        value={values.last_name}
                        type='text'
                        size={100}
                        placeholder='Last name'
                        className={cn(
                          ' text-base font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content?.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.first_name && touched.first_name && 'border-red-500'
                        )}
                        data-testid='edit-lastname'
                      />
                    </FormControl>
                  </FormItem>
                </div>
                <FormItem className='flex flex-col'>
                  <DatePicker
                    value={values.dob as Date}
                    onChange={(value) => {
                      handleChange({
                        target: {
                          name: 'dob',
                          value,
                        },
                      });
                    }}
                  />
                </FormItem>
                <FormItem>
                  <FormControl>
                    <Select
                      name='gender'
                      defaultValue={values.gender}
                      onValueChange={(value) => {
                        handleChange({
                          target: {
                            name: 'gender',
                            value,
                          },
                        });
                      }}
                      data-testid='edit-gender'
                    >
                      <SelectTrigger
                        className={cn(
                          ' text-base font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content?.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.first_name && touched.first_name && 'border-red-500'
                        )}
                      >
                        <SelectValue placeholder='Gender' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='male'>Male</SelectItem>
                        <SelectItem value='female'>Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
                <FormItem className='text-sm'>
                  <FormControl>
                    <Input
                      name='email'
                      value={values.email}
                      type='email'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      size={100}
                      placeholder='Email'
                      className={cn(
                        ' text-base font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                        !isEmpty(content?.colorScheme) &&
                          'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                        errors.first_name && touched.first_name && 'border-red-500'
                      )}
                      data-testid='edit-email'
                    />
                  </FormControl>
                  <FieldErrorMessage error={errors.email} />
                </FormItem>
                <FormItem className='text-sm'>
                  <FormControl>
                    <Input
                      name='phone'
                      value={values.phone}
                      type='text'
                      onBlur={handleBlur}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      maxLength={20}
                      placeholder='Phone number'
                      className={cn(
                        ' text-base font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                        !isEmpty(content?.colorScheme) &&
                          'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                        errors.first_name && touched.first_name && 'border-red-500'
                      )}
                      data-testid='edit-phone'
                    />
                  </FormControl>
                  <FieldErrorMessage error={errors.phone} />
                </FormItem>
              </Form>
              <ModalResponsiveFooter>
                <Button
                  onClick={() => setOpenModal?.(false)}
                  type='button'
                  variant='outline'
                  className='hidden md:flex'
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  onClick={() => handleSubmit()}
                  style={
                    !isEmpty(content?.colorScheme)
                      ? {
                          backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                          color: content?.colorScheme?.btnSolidlabel,
                        }
                      : {}
                  }
                >
                  Save
                </Button>
              </ModalResponsiveFooter>
            </>
          )}
        </Formik>
      </div>
    </ModalResponsive>
  );
}
